import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  checkUpdateFailure,
  ContentfulLibraryService,
  ContentfulSpaceTagId,
  IContentfulState,
  init,
  retry,
  syncAssets,
  syncAssetsFailure,
  syncAssetsSuccess,
  syncEntries,
  syncEntriesFailure,
  syncEntriesSuccess,
} from '@qld-recreational/contentful-library';
import {
  catchError,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { selectNextSyncToken } from './contentful.reducer';
import { of, throwError } from 'rxjs';
import { switchContentfulContext } from '../settings/settings.actions';
import { BackgroundRequestService } from '../background-request/background-request.service';
import { selectContentfulContext } from '../settings/settings.selectors';

@Injectable()
export class ContentfulEffects {
  public checkUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(retry, init),
      tap(() => this.contentfulService.preventScreenSleep()),
      switchMap(() =>
        this.backgroundRequestService.checkConnectivity().pipe(
          withLatestFrom(
            this.contentfulStore.select(selectContentfulContext),
            this.contentfulStore.select(selectNextSyncToken),
          ),
          switchMap(([connected, contentfulContext, nextSyncToken]) =>
            connected
              ? this.contentfulService
                  .checkRemoteUpdate(
                    ContentfulSpaceTagId.COMMERCIAL,
                    contentfulContext,
                    nextSyncToken,
                  )
                  .pipe(
                    map((syncCollection) => syncEntries({ syncCollection })),
                  )
              : throwError(() => new Error('No Internet Connection')),
          ),
          catchError((err) =>
            of(
              checkUpdateFailure({
                error: err?.message || 'Sync Entries Error',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public changeStage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(switchContentfulContext),
        switchMap(() => this.contentfulService.clearCache()),
      ),
    { dispatch: false },
  );

  public syncEntries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(syncEntries),
      switchMap(({ syncCollection }) =>
        this.contentfulService.syncEntries(syncCollection).pipe(
          map((collection) =>
            syncEntriesSuccess({ syncCollection: collection }),
          ),
          catchError((err) =>
            of(
              syncEntriesFailure({
                error: err?.message || 'Sync Entries Error',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public syncEntriesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(syncEntriesSuccess),
      map((syncCollection) => syncAssets(syncCollection)),
    ),
  );

  public syncAssets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(syncAssets),
      switchMap(({ syncCollection }) =>
        this.contentfulService.syncAssets(syncCollection).pipe(
          map((nextSyncToken) => syncAssetsSuccess({ nextSyncToken })),
          catchError((err) =>
            of(
              syncAssetsFailure({ error: err?.message || 'Sync Assets Error' }),
            ),
          ),
        ),
      ),
    ),
  );

  public syncAssetsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(syncAssetsSuccess),
        tap(() => this.contentfulService.allowScreenSleep()),
        map(({ nextSyncToken }) =>
          this.contentfulService.updateNextToken(nextSyncToken),
        ),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private contentfulStore: Store<IContentfulState>,
    private contentfulService: ContentfulLibraryService,
    private backgroundRequestService: BackgroundRequestService,
  ) {}
}
